/**
 * Record-level validation shared by every path that turns a batch of signals
 * (message path + topic records) into one visualizer panel: the on-page
 * workspace action handlers and the off-page deep-link builder. Keeping the
 * rules here prevents the two paths from drifting apart — the model must
 * observe the same outcome for the same input regardless of which page the
 * user is on.
 *
 * Node-level rules that need a hydrated topic tree (e.g. log column-conflict
 * checks) stay with the on-page handlers; everything expressible from raw
 * records lives here.
 */
import { PanelType } from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

import {
  CanonicalDataType,
  getCorrespondingGeoType,
  isGeoSpecificType,
  isLatitudeType,
  isLongitudeType,
  isPlottableCanonicalType,
  MessagePathRecord,
  RepresentationStorageFormat,
  TopicRecord,
} from "../topics";

export interface SignalEntry {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
}

export interface InvalidSignal {
  messagePathId: string;
  reason: string;
}

/** The panel types creatable from message paths. */
export type SignalPanelType =
  | PanelType.Plot
  | PanelType.Log
  | PanelType.RawMessage
  | PanelType.Image
  | PanelType.Map;

export type SignalsValidation =
  | {
      ok: true;
      panelType: SignalPanelType;
      /**
       * The entries to create the panel from. May be longer than the input:
       * a lone latitude/longitude signal gets its corresponding pair member
       * bundled in, just as dragging a lone coordinate onto the workspace
       * does.
       */
      entries: SignalEntry[];
    }
  | { ok: false; error: string; invalid: InvalidSignal[] };

/**
 * Whether a signal can be read in one of `formats`, mirroring
 * `MessagePathNode.findRepresentation`'s resolution order: the path's own
 * representations, then ancestor paths' (parent chain), then the topic's
 * default representation. Paths commonly carry no representations of their
 * own and inherit the topic default (e.g. ULog console_logs fields).
 */
function hasRepresentation(
  entry: SignalEntry,
  formats: RepresentationStorageFormat[],
): boolean {
  const { messagePath, topic } = entry;
  const matches = (
    representations: { storage_format: RepresentationStorageFormat }[],
  ) =>
    representations.some((representation) =>
      formats.includes(representation.storage_format),
    );

  if (matches(messagePath.representations)) {
    return true;
  }

  const isAncestor = (candidate: MessagePathRecord) =>
    candidate.path_in_schema.length < messagePath.path_in_schema.length &&
    candidate.path_in_schema.every(
      (segment, i) => messagePath.path_in_schema[i] === segment,
    );
  if (
    topic.message_paths.some(
      (candidate) =>
        isAncestor(candidate) && matches(candidate.representations),
    )
  ) {
    return true;
  }

  return (
    topic.default_representation !== null &&
    topic.default_representation !== undefined &&
    formats.includes(topic.default_representation.storage_format)
  );
}

/**
 * A record is a leaf when no other path in its topic extends its
 * path_in_schema. Mirrors MessagePathNode.isLeafNode() without needing a
 * hydrated tree.
 */
function isLeafRecord(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): boolean {
  const prefix = messagePath.path_in_schema;
  return !topic.message_paths.some(
    (candidate) =>
      candidate.path_in_schema.length > prefix.length &&
      prefix.every((segment, i) => candidate.path_in_schema[i] === segment),
  );
}

/**
 * Find the sibling record that completes a latitude/longitude pair: same
 * parent path, complementary geo type. Mirrors the drag-and-drop path's
 * `getCorrespondingMessagePath`, which searches the node's parent's children.
 */
export function findCorrespondingGeoRecord(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): MessagePathRecord | undefined {
  const correspondingTypes = getCorrespondingGeoType(
    messagePath.canonical_data_type,
  );
  const parentPath = messagePath.path_in_schema.slice(0, -1);
  return topic.message_paths.find(
    (candidate) =>
      candidate.message_path_id !== messagePath.message_path_id &&
      correspondingTypes.includes(candidate.canonical_data_type) &&
      candidate.path_in_schema.length === parentPath.length + 1 &&
      parentPath.every((segment, i) => candidate.path_in_schema[i] === segment),
  );
}

/**
 * Validate a single signal against the panel type it would join. Applied both
 * when creating a panel and when adding to an existing one, so the two
 * operations accept exactly the same signals. Returns a reason when the
 * signal cannot render on that panel type.
 *
 * Each arm checks the signal's data type as well as its structure. The data
 * type used to be guaranteed by the caller — a panel type derived from the
 * signals could only ever be one they suited — but a caller naming the panel
 * type explicitly (see {@link validateSignalsForNewPanel}) carries no such
 * guarantee, and without these checks an unrenderable panel is created and
 * reported as a success.
 */
export function validateSignalForPanelType(
  panelType: SignalPanelType,
  entry: SignalEntry,
): string | undefined {
  const { messagePath, topic } = entry;
  const canonicalType = messagePath.canonical_data_type;
  switch (panelType) {
    case PanelType.Plot:
      // Wider than the default rule on purpose: `arrayDefaultsToPlot`
      // decides what to open unasked, this decides what to render once
      // something named the panel — `panel_type`, add-to-panel, or a drag onto
      // an existing plot. So a byte array plots on request, never by default.
      if (!isPlottableCanonicalType(canonicalType)) {
        return `Data type "${canonicalType}" cannot be plotted.`;
      }
      if (!isLeafRecord(topic, messagePath)) {
        return `Message path "${messagePath.message_path}" has nested fields and cannot be plotted directly; plot one of its leaf fields instead.`;
      }
      return undefined;
    case PanelType.Image:
      if (canonicalType !== CanonicalDataType.Image) {
        return `Data type "${canonicalType}" is not an image.`;
      }
      if (!hasRepresentation(entry, [RepresentationStorageFormat.MCAP])) {
        return "The Image panel does not yet support visualizing this signal.";
      }
      return undefined;
    case PanelType.Log:
    case PanelType.RawMessage:
      // A log column renders through the same readers as a raw message, but
      // an image is never one; `isViewableAsConsoleLog` excludes it too.
      if (
        panelType === PanelType.Log &&
        canonicalType === CanonicalDataType.Image
      ) {
        return "An image cannot be shown as a log column.";
      }
      if (
        !hasRepresentation(entry, [
          RepresentationStorageFormat.MCAP,
          RepresentationStorageFormat.PARQUET,
        ])
      ) {
        return `Message path "${messagePath.message_path}" has no readable representation.`;
      }
      return undefined;
    case PanelType.Map:
      // Geo-specific only. `isViewableAsMap` deliberately excludes plain
      // numbers (ENG-2040: no UX yet for assigning paths to lat/lon slots),
      // and letting one through here would also reach
      // `getCorrespondingGeoType`, which throws for every other type.
      if (!isGeoSpecificType(canonicalType)) {
        return `Data type "${canonicalType}" cannot be shown on a map; a map needs a latitude/longitude pair.`;
      }
      return undefined;
    default: {
      const _exhaustive: never = panelType;
      return `Unhandled panel type: ${String(_exhaustive)}`;
    }
  }
}

/**
 * Validate a batch of signals destined for one NEW panel.
 *
 * 1. Rejects duplicates and mixed panel types with per-signal reasons.
 * 2. Rejects signals the resolved panel type cannot render
 *    ({@link validateSignalForPanelType}).
 * 3. Enforces same-topic constraints (log: topic name; raw message: topic id).
 * 4. For maps, requires a same-topic latitude/longitude pair — and bundles
 *    the pair member automatically when a lone lat/lon is given.
 *
 * `requestedPanelType` names the panel to build instead of the one the
 * signals' data types imply — a caller asking to plot a signal that defaults
 * to a raw view, or to read a plottable signal as raw text. It settles which
 * panel type the batch targets and so retires rule 1's mixed-type check;
 * every other rule still applies, and a signal the requested type cannot
 * render is rejected exactly as it would be by default.
 */
export function validateSignalsForNewPanel(
  inputEntries: SignalEntry[],
  requestedPanelType?: SignalPanelType,
): SignalsValidation {
  if (inputEntries.length === 0) {
    return {
      ok: false,
      error: "At least one message path is required to create a panel.",
      invalid: [],
    };
  }

  const invalid: InvalidSignal[] = [];

  const seen = new Set<string>();
  const entries: SignalEntry[] = [];
  for (const entry of inputEntries) {
    const id = entry.messagePath.message_path_id;
    if (seen.has(id)) {
      invalid.push({
        messagePathId: id,
        reason:
          "Duplicated in the request; each message path may appear only once.",
      });
      continue;
    }
    seen.add(id);
    entries.push(entry);
  }
  if (invalid.length > 0) {
    return {
      ok: false,
      error: "Some message paths cannot be opened.",
      invalid,
    };
  }

  let panelType: SignalPanelType;
  if (requestedPanelType !== undefined) {
    panelType = requestedPanelType;
  } else {
    const typed = entries.map((entry) => ({
      entry,
      panelType: determineDefaultPanelType(entry.messagePath),
    }));
    panelType = typed[0].panelType as SignalPanelType;
    if (typed.some((item) => item.panelType !== panelType)) {
      return {
        ok: false,
        error:
          "All message paths in one call must visualize as the same panel type. " +
          "Split the request into one call per panel, or name the panel type to " +
          "open them together.",
        invalid: typed.map((item) => ({
          messagePathId: item.entry.messagePath.message_path_id,
          reason: `Visualizes as a ${item.panelType} panel.`,
        })),
      };
    }
  }

  entries.forEach((entry) => {
    const reason = validateSignalForPanelType(panelType, entry);
    if (reason) {
      invalid.push({
        messagePathId: entry.messagePath.message_path_id,
        reason,
      });
    }
  });
  if (invalid.length > 0) {
    return {
      ok: false,
      error: `Some message paths cannot be shown on a ${panelType} panel.`,
      invalid,
    };
  }

  if (panelType === PanelType.Log || panelType === PanelType.RawMessage) {
    const first = entries[0].topic;
    entries.slice(1).forEach((entry) => {
      const sameTopic =
        panelType === PanelType.Log
          ? entry.topic.topic_name === first.topic_name
          : entry.topic.topic_id === first.topic_id;
      if (!sameTopic) {
        invalid.push({
          messagePathId: entry.messagePath.message_path_id,
          reason: `Cannot combine data from different topics on a ${panelType} panel.`,
        });
      }
    });
    if (invalid.length > 0) {
      return {
        ok: false,
        error: `All message paths on a ${panelType} panel must come from one topic.`,
        invalid,
      };
    }
  }

  if (panelType === PanelType.Map) {
    return validateMapSignals(entries);
  }

  return { ok: true, panelType, entries };
}

/**
 * A map path renders exactly one latitude/longitude pair, so a new map panel
 * accepts one pair: two complementary same-topic paths, or a single path
 * whose pair member can be found in the same topic and bundled in.
 */
function validateMapSignals(entries: SignalEntry[]): SignalsValidation {
  if (entries.length > 2) {
    return {
      ok: false,
      error:
        "A new map panel accepts at most one latitude/longitude pair. " +
        "Open the panel with the pair, then add further paths with the add-to-panel tool.",
      invalid: entries.slice(2).map((entry) => ({
        messagePathId: entry.messagePath.message_path_id,
        reason:
          "Exceeds the one latitude/longitude pair a new map panel accepts.",
      })),
    };
  }

  if (entries.length === 2) {
    const [a, b] = entries;
    const types: [CanonicalDataType, CanonicalDataType] = [
      a.messagePath.canonical_data_type,
      b.messagePath.canonical_data_type,
    ];
    const isPair =
      (isLatitudeType(types[0]) && isLongitudeType(types[1])) ||
      (isLongitudeType(types[0]) && isLatitudeType(types[1]));
    if (!isPair) {
      return {
        ok: false,
        error:
          "Two message paths on a new map panel must form a latitude/longitude pair.",
        invalid: [
          {
            messagePathId: b.messagePath.message_path_id,
            reason: "Does not complete a latitude/longitude pair.",
          },
        ],
      };
    }
    if (a.topic.topic_id !== b.topic.topic_id) {
      return {
        ok: false,
        error:
          "A map panel's latitude/longitude pair must come from one topic; " +
          "coordinates from different topics cannot be joined into one path.",
        invalid: [
          {
            messagePathId: b.messagePath.message_path_id,
            reason: `Belongs to a different topic than "${a.messagePath.message_path}".`,
          },
        ],
      };
    }
    // Complementary types alone don't make a pair: pose.latitude +
    // home.longitude would render an incorrect track. Require sibling fields
    // of one parent, the same structural guarantee drag-and-drop pairing has.
    const parentOfA = a.messagePath.path_in_schema.slice(0, -1);
    const parentOfB = b.messagePath.path_in_schema.slice(0, -1);
    const sameParent =
      parentOfA.length === parentOfB.length &&
      parentOfA.every((segment, i) => parentOfB[i] === segment);
    if (!sameParent) {
      return {
        ok: false,
        error:
          "A map panel's latitude/longitude pair must be sibling fields of the " +
          "same parent path; unrelated coordinate streams cannot form one track.",
        invalid: [
          {
            messagePathId: b.messagePath.message_path_id,
            reason: `Is not a sibling of "${a.messagePath.message_path}".`,
          },
        ],
      };
    }
    return { ok: true, panelType: PanelType.Map, entries };
  }

  // Single entry: bundle the pair member, as drag-and-drop does.
  const [only] = entries;
  const sibling = findCorrespondingGeoRecord(only.topic, only.messagePath);
  if (!sibling) {
    return {
      ok: false,
      error:
        `No matching pair coordinate was found for "${only.messagePath.message_path}" ` +
        `in topic "${only.topic.topic_name}"; a map panel needs a latitude/longitude pair.`,
      invalid: [
        {
          messagePathId: only.messagePath.message_path_id,
          reason: "Missing the corresponding latitude/longitude path.",
        },
      ],
    };
  }
  return {
    ok: true,
    panelType: PanelType.Map,
    entries: [only, { messagePath: sibling, topic: only.topic }],
  };
}
